import * as React from "react"

import { cn } from "@/lib/utils"

type InputProps = React.ComponentProps<"input"> & {
  // Fixed, non-editable content shown before the editable value -- e.g. a
  // URL host prefix. Renders the input inside a bordered wrapper instead of
  // giving the input its own border, so the whole group reads as one field:
  // one focus ring, one aria-invalid ring, one disabled treatment, shared
  // via `has-[]` on the wrapper (see components/ui/file-input for the same
  // pattern). Do not hand-roll this shape at the call site -- extend it here.
  startAdornment?: React.ReactNode
}

function Input({ className, type, startAdornment, ...props }: InputProps) {
  if (startAdornment) {
    return (
      <div
        className={cn(
          "border-input dark:bg-input/30 flex h-11 md:h-9 w-full min-w-0 items-center gap-2 rounded-md border bg-transparent px-3 shadow-xs transition-[color,box-shadow]",
          "has-[input:focus-visible]:border-ring has-[input:focus-visible]:ring-ring/50 has-[input:focus-visible]:ring-[3px]",
          "has-[input[aria-invalid=true]]:ring-destructive/20 dark:has-[input[aria-invalid=true]]:ring-destructive/40 has-[input[aria-invalid=true]]:border-destructive",
          "has-[input:disabled]:pointer-events-none has-[input:disabled]:cursor-not-allowed has-[input:disabled]:opacity-50",
          className
        )}
      >
        <span className="text-muted-foreground shrink-0 select-none text-base md:text-sm" aria-hidden="true">
          {startAdornment}
        </span>
        <input
          type={type}
          data-slot="input"
          className="text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground min-w-0 flex-1 border-0 bg-transparent p-0 text-base outline-none md:text-sm"
          {...props}
        />
      </div>
    )
  }

  return (
    <input
      type={type}
      data-slot="input"
      className={cn(
        "file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input h-11 md:h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
        "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
        className
      )}
      {...props}
    />
  )
}

export { Input }
